import { NavigateFunction } from 'react-router-dom';
import { IPositionSummary } from '../../../../Models/IPositionSummary';
import { Constants } from '../../../../Models/Constants';
import { setLocalStorageItem } from '../../../../Services/Global';

/**
 * Factory function to create navigation handlers with proper closure scope
 */
export const createNavigationHandlers = (
  setExpandedItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  setSelectedIndustry: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedSubIndustry: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedApplication: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedSubApplication: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedKey: React.Dispatch<React.SetStateAction<string>>,
  setDisplaySummaries: React.Dispatch<React.SetStateAction<boolean>>,
  setActiveCardId: React.Dispatch<React.SetStateAction<string | null>>
) => {
  /**
   * Handle toggle of navigation items
   */
  const handleToggleItem = (itemName: string, fullPath?: string) => {
    const toggleKey = fullPath || itemName;
    setExpandedItems((prev) => ({
      ...prev,
      [toggleKey]: !prev[toggleKey],
    }));
  };

  /**
   * Handle subapplication selection
   */
  const handleSubApplicationClick = (
    industry: string,
    subIndustry: string,
    application: string,
    subApplication: string
  ) => {
    // Reset active card immediately
    setActiveCardId(null);

    // Update selected navigation items
    setSelectedIndustry(industry);
    setSelectedKey(subApplication);
    setLocalStorageItem(Constants.Industry, industry);
    
    setSelectedSubIndustry(subIndustry);
    setLocalStorageItem(Constants.SubIndustry, subIndustry);
    
    setSelectedApplication(application);
    setLocalStorageItem(Constants.Application, application);
    
    setSelectedSubApplication(subApplication);
    setLocalStorageItem(Constants.SubApplication, subApplication);
    
    setDisplaySummaries(true);
  };

  return {
    handleToggleItem,
    handleSubApplicationClick
  };
};

/**
 * Factory function to create card interaction handlers
 */
export const createCardHandlers = (
  isModeler: boolean,
  isSme: boolean,
  navigate: NavigateFunction,
  setActiveCardId: React.Dispatch<React.SetStateAction<string | null>>
) => {
  /**
   * Handle card click for navigation
   */
  const handleCardClick = (cardId: string, item: IPositionSummary) => {
    if (isModeler && !isSme && item.summary["todo"] > 0) {
      return;
    }
    setActiveCardId(cardId);
    navigate(`/blindtests/${item.blindTestDataSetId}`, {
      state: { positionSummary: item },
    });
  };

  /**
   * Generate card ID for consistency
   */
  const generateCardId = (item: IPositionSummary): string => {
    return `${item.alfaLavalPosition}-${item.productLine}-${item.blindTestDataSetId}`;
  };

  return {
    handleCardClick,
    generateCardId
  };
};

/**
 * Factory function to create download handlers
 */
export const createDownloadHandlers = (
  setDownloadingItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setShowError: React.Dispatch<React.SetStateAction<boolean>>
) => {
  /**
   * Set downloading state for a specific card
   */
  const setCardDownloading = (cardId: string, isDownloading: boolean) => {
    setDownloadingItems((prev) => ({
      ...prev,
      [cardId]: isDownloading,
    }));
  };

  /**
   * Handle download errors
   */
  const handleDownloadError = (error: Error | string, context?: any) => {
    const errorMessage = error instanceof Error ? error.message : error;
    setError(errorMessage);
    setShowError(true);
  };

  /**
   * Reset download state for all cards
   */
  const resetDownloadStates = () => {
    setDownloadingItems({});
  };

  return {
    setCardDownloading,
    handleDownloadError,
    resetDownloadStates
  };
};

/**
 * Factory function to create error handlers
 */
export const createErrorHandlers = (
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setShowError: React.Dispatch<React.SetStateAction<boolean>>
) => {
  /**
   * Handle error snackbar close
   */
  const handleCloseError = () => {
    setShowError(false);
  };

  /**
   * Set error with automatic display
   */
  const setErrorWithDisplay = (errorMessage: string) => {
    setError(errorMessage);
    setShowError(true);
  };

  /**
   * Clear all errors
   */
  const clearAllErrors = () => {
    setError(null);
    setShowError(false);
  };

  return {
    handleCloseError,
    setErrorWithDisplay,
    clearAllErrors
  };
};

/**
 * Factory function to create state management handlers
 */
export const createStateHandlers = (
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setDisplaySummaries: React.Dispatch<React.SetStateAction<boolean>>,
  setActiveCardId: React.Dispatch<React.SetStateAction<string | null>>,
  setPositionSummaries: React.Dispatch<React.SetStateAction<IPositionSummary[]>>,
  setNavItems: React.Dispatch<React.SetStateAction<any[]>>
) => {
  /**
   * Reset all component state
   */
  const resetAllState = () => {
    setIsLoading(true);
    setDisplaySummaries(false);
    setActiveCardId(null);
    setPositionSummaries([]);
    setNavItems([]);
  };

  /**
   * Set active card with default selection logic
   */
  const setActiveCardWithDefault = (summaries: IPositionSummary[], currentActiveCardId: string | null) => {
    if (summaries.length > 0 && !currentActiveCardId) {
      // Default to first card with "ToDo" or the first card overall
      const defaultCard =
        summaries.find((item) => item.summary["todo"] > 0) || 
        summaries[0];
        
      if (defaultCard) {
        const cardId = `${defaultCard.alfaLavalPosition}-${defaultCard.productLine}-${defaultCard.blindTestDataSetId}`;
        setActiveCardId(cardId);
      }
    }
  };

  /**
   * Update position summaries and set default active card
   */
  const updatePositionSummaries = (summaries: IPositionSummary[], currentActiveCardId: string | null) => {
    setPositionSummaries(summaries);
    setActiveCardWithDefault(summaries, currentActiveCardId);
  };

  return {
    resetAllState,
    setActiveCardWithDefault,
    updatePositionSummaries
  };
};

/**
 * Factory function to create initialization handlers
 */
export const createInitializationHandlers = (
  setSelectedIndustry: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedSubIndustry: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedApplication: React.Dispatch<React.SetStateAction<string | undefined>>,
  setSelectedSubApplication: React.Dispatch<React.SetStateAction<string | undefined>>,
  setDisplaySummaries: React.Dispatch<React.SetStateAction<boolean>>
) => {
  /**
   * Initialize from local storage
   */
  const initializeFromLocalStorage = (
    getLocalStorageItem: (key: string) => string | null
  ) => {
    const storedIndustry = getLocalStorageItem(Constants.Industry);
    const storedSubIndustry = getLocalStorageItem(Constants.SubIndustry);
    const storedApplication = getLocalStorageItem(Constants.Application);
    const storedSubApplication = getLocalStorageItem(Constants.SubApplication);

    if (
      storedIndustry &&
      storedSubIndustry &&
      storedApplication &&
      storedSubApplication
    ) {
      setSelectedIndustry(storedIndustry);
      setSelectedSubIndustry(storedSubIndustry);
      setSelectedApplication(storedApplication);
      setSelectedSubApplication(storedSubApplication);
      setDisplaySummaries(true);
      return true;
    }
    return false;
  };

  return {
    initializeFromLocalStorage
  };
}; 